import React, { useContext, useEffect, useRef, useState } from 'react';
import { Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Location } from 'react-router';
import { PageHeader, Section, SpinnerArea } from '../../../../../../shared/components';
import OperationContext from '../../../context';
import MainContext from '../../../../../App/MainProvider/context';
import RouteLeavingGuard from '../../../../../App/RouteLeavingGuard';
import OperationSubtitle from '../../../OperationSubtitle';
import useQuery from '../../../../../../shared/hooks/useQuery';
import SectorTabContainer from './SectorTabContainer';
import { SectorTabContainerBaseHandle } from '../Shared/SectorTabContainerBase';
import {
  calculateBuildingNotEstimatedEmissions,
  calculateEmissionsPurchasedElectricity,
  calculateEmissionsRoadMaintenanceActivities,
  calculateLandUseChange,
  calculateRoadPavement,
  calculateRoadPavementTransport,
  setBuildingNotEstimatedEmissions,
} from '../../Utils/moduleCalculates';
import TransportRoadsCalculatorContext from './TransportRoadsCalculatorContext';

const TransportRoadsCalculator: React.FC = () => {
  const { operation, loading } = useContext(OperationContext);

  const {
    calculateId,
    landUseChange,
    roadPavementTransport,
    emissionsRoadMaintenanceActivities,
    emissionsDevelopmentBuildingConstruction,
    emissionsPurchasedElectricity,
    totalConstructionOverriden,
    totalOperationOverriden,
    isFinished,
  } = useContext(TransportRoadsCalculatorContext);

  const location = useLocation();
  const { t } = useTranslation();
  const { service } = useContext(MainContext);

  const sectorTabRef = useRef<SectorTabContainerBaseHandle>(null);

  // construction
  const [updatedLandUseChange, setUpdatedLandUseChange] = useState(landUseChange);
  const [
    updatedEmissionsDevelopmentBuildingConstruction,
    setUpdatedEmissionsDevelopmentBuildingConstruction,
  ] = useState(emissionsDevelopmentBuildingConstruction);
  const [updatedRoadPavementTransport, setUpdatedRoadPavementTransport] =
    useState(roadPavementTransport);

  // operation
  const [updatedEmissionsRoadMaintenanceActivities, setUpdatedEmissionsRoadMaintenanceActivities] =
    useState(emissionsRoadMaintenanceActivities);
  const [updatedEmissionsPurchasedElectricity, setUpdatedEmissionsPurchasedElectricity] = useState(
    emissionsPurchasedElectricity
  );

  const updateTotalCarbonBuildingConstruction = () => {
    if (
      emissionsDevelopmentBuildingConstruction.isEstimated === false &&
      emissionsDevelopmentBuildingConstruction.totalArea
    ) {
      const updatedConstruction = setBuildingNotEstimatedEmissions(
        emissionsDevelopmentBuildingConstruction,
        1
      );

      const result = calculateBuildingNotEstimatedEmissions(updatedConstruction);

      updatedConstruction.subTotal = result;
      setUpdatedEmissionsDevelopmentBuildingConstruction(updatedConstruction);
    }
  };

  const updateTotalCarbonLandUseChange = () => {
    const recalculatedRows = landUseChange.rows.map((x) => {
      const totalCarbon = calculateLandUseChange(x.aboveGBCarbon, x.belowGBCarbon, x.area);
      return {
        ...x,
        totalCarbon,
      };
    });
    landUseChange.rows = recalculatedRows;
    const totalCarbonSum = recalculatedRows.reduce((sum, row) => sum + row.totalCarbon, 0);
    landUseChange.subTotal = recalculatedRows.length > 0 ? totalCarbonSum * (44 / 12) : null;
    setUpdatedLandUseChange(landUseChange);
  };

  const updateTotalRoadPavementTransport = () => {
    if (roadPavementTransport.isTotalAreaKnown !== null) {
      const updatedData = { ...roadPavementTransport };

      if (roadPavementTransport.isTotalAreaKnown === true && roadPavementTransport.totalArea) {
        const factorValue =
          roadPavementTransport.factorValue / roadPavementTransport.sqFeetToSqMeters;
        let result = calculateRoadPavement(factorValue, roadPavementTransport.totalArea);

        if (
          roadPavementTransport.totalAreaRoadRehabilitated &&
          roadPavementTransport.sqDefaultEmission
        ) {
          const SqEmissionFactor =
            roadPavementTransport.sqDefaultEmission / roadPavementTransport.sqFeetToSqMeters;
          result = calculateRoadPavementTransport(
            result,
            roadPavementTransport.totalAreaRoadRehabilitated,
            SqEmissionFactor
          );
        }

        updatedData.subTotal = Math.round(result);
      }

      if (
        roadPavementTransport.isTotalAreaKnown === false &&
        roadPavementTransport.pavedRoadLength &&
        roadPavementTransport.numberOfLanes
      ) {
        const factor = roadPavementTransport.factorValue / roadPavementTransport.sqFeetToSqMeters;
        const resultingRoadlen =
          roadPavementTransport.numberOfLanes * roadPavementTransport.factorWidth;
        const pavedRoad = roadPavementTransport.pavedRoadLength * 1000;
        const areaCalculated = resultingRoadlen * pavedRoad;
        let result = calculateRoadPavement(factor, areaCalculated);

        if (
          roadPavementTransport.totalAreaRoadRehabilitated &&
          roadPavementTransport.sqDefaultEmission
        ) {
          const SqEmissionFactor =
            roadPavementTransport.sqDefaultEmission / roadPavementTransport.sqFeetToSqMeters;
          result = calculateRoadPavementTransport(
            result,
            roadPavementTransport.totalAreaRoadRehabilitated,
            SqEmissionFactor
          );
        }

        updatedData.subTotal = Math.round(result);
      }

      setUpdatedRoadPavementTransport(updatedData);
    }
  };

  const updateTotalEmissionsRoadMaintenanceActivities = () => {
    if (emissionsRoadMaintenanceActivities.area !== null) {
      const result = calculateEmissionsRoadMaintenanceActivities(
        emissionsRoadMaintenanceActivities.factorValue,
        emissionsRoadMaintenanceActivities.area
      );
      emissionsRoadMaintenanceActivities.subTotal = result;
    }

    setUpdatedEmissionsRoadMaintenanceActivities(emissionsRoadMaintenanceActivities);
  };

  const updateTotalEmissionsPurchasedElectricity = () => {
    if (emissionsPurchasedElectricity.amountElectricityConsumed !== null) {
      const result = calculateEmissionsPurchasedElectricity(
        emissionsPurchasedElectricity.countryFactor,
        emissionsPurchasedElectricity.amountElectricityConsumed
      );
      emissionsPurchasedElectricity.subTotal = result;
    }

    setUpdatedEmissionsPurchasedElectricity(emissionsPurchasedElectricity);
  };

  useEffect(() => {
    if (landUseChange.rows && landUseChange.rows.length > 0) {
      updateTotalCarbonLandUseChange();
    } else {
      landUseChange.rows = [];
      setUpdatedLandUseChange(landUseChange);
    }

    if (emissionsDevelopmentBuildingConstruction) {
      updateTotalCarbonBuildingConstruction();
    }

    if (roadPavementTransport) {
      updateTotalRoadPavementTransport();
    }

    if (emissionsRoadMaintenanceActivities.area) {
      updateTotalEmissionsRoadMaintenanceActivities();
    }

    if (emissionsPurchasedElectricity.amountElectricityConsumed) {
      updateTotalEmissionsPurchasedElectricity();
    }
  }, []);

  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [localTotalConstructionOverriden, setTotalConstructionOverriden] = useState<number | null>(
    totalConstructionOverriden
  );
  const [localTotalOperationOverriden, setTotalOperationOverriden] = useState<number | null>(
    totalOperationOverriden
  );

  useEffect(() => {
    setTotalConstructionOverriden(totalConstructionOverriden);
    setTotalOperationOverriden(totalOperationOverriden);
  }, [totalConstructionOverriden, totalOperationOverriden]);

  const queryParams = useQuery();
  const from = queryParams.get('from') || undefined;
  const title = `Operation ${operation.number} / GHG Calculator`;
  const navigate = useNavigate();

  const saveData = (targetLocation?: Location | null) => {
    const callback = () => {
      setIsSaveDisabled(true);
      if (targetLocation) {
        navigate(targetLocation);
      }
    };

    service(() => sectorTabRef.current!.saveChanges(), { callback });
  };

  return (
    <>
      <RouteLeavingGuard
        when={!isSaveDisabled}
        shouldBlockNavigation={() => !isSaveDisabled}
        from={location}
        onSave={(targetLocation) => saveData(targetLocation)}
        message={t('dirtyIndicatorsPopupMsg')}
      />
      <Container className="rolesAndPermissions body-calculator-color">
        <PageHeader title={title} backUrl={from}>
          <OperationSubtitle />
        </PageHeader>
        <SpinnerArea loading={loading}>
          <Row>
            <Section>
              <SectorTabContainer
                ref={sectorTabRef}
                calculatorId={calculateId != null ? calculateId : 0}
                operation={operation}
                landUseChange={updatedLandUseChange}
                roadPavementTransport={updatedRoadPavementTransport}
                emissionsRoadMaintenanceActivities={updatedEmissionsRoadMaintenanceActivities}
                emissionsDevelopmentBuildingConstruction={
                  updatedEmissionsDevelopmentBuildingConstruction
                }
                emissionsPurchasedElectricity={updatedEmissionsPurchasedElectricity}
                loading={loading}
                isSaveDisabled={isSaveDisabled}
                setIsSaveDisabled={setIsSaveDisabled}
                totalConstructionOverriden={localTotalConstructionOverriden}
                setTotalConstructionOverriden={setTotalConstructionOverriden}
                totalOperationOverriden={localTotalOperationOverriden}
                setTotalOperationOverriden={setTotalOperationOverriden}
                isFinished={isFinished}
              />
            </Section>
          </Row>
        </SpinnerArea>
      </Container>
    </>
  );
};

export default TransportRoadsCalculator;
